import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/auth.context";
import {
  PACKAGE_CATEGORY_LABELS,
  contentFor,
  effectiveDiscountPercent,
  packageFranchiseeDiscountPercentOf,
  type Currency,
  type Locale,
  type Package,
  type Product,
} from "@nh/shared";
import { usePriceViewer } from "@/features/shop/usePriceViewer";
import { Button, Divider } from "@/shared/ui/core";
import {
  packagePriceFor,
  registerHrefFor,
  resolveItems,
  totalUnits,
} from "../packages.catalog";
import { PackageContents } from "./PackageContents";
import { PackageArtwork } from "./PackageArtwork";
import { PackageDiscountBadge } from "./PackageDiscountBadge";
import { PackageFranchiseeNote } from "./PackageFranchiseeNote";
import { PackagePrice } from "./PackagePrice";
import { PackageVolumePoints } from "./PackageVolumePoints";

export interface PackageCardProps {
  pkg: Package;
  /** Moneda activa del sitio: la del selector de la barra superior. */
  currency: Currency;
  /** Idioma del CONTENIDO (viene del servidor, no de i18n). */
  locale: Locale;
  /** Locale de formato de números ("es-MX" / "en-US"). */
  numberLocale: string;
  /** El catálogo indexado, para poner nombre a los productos. `null` mientras viaja. */
  productsById: Map<string, Product> | null;
}

/**
 * La tarjeta de un paquete.
 *
 * ── La tarjeta NO es un enlace, el botón sí ──────────────────────────
 * Al revés que la del catálogo. Ahí la tarjeta entera llevaba a la ficha,
 * un destino inocuo; aquí el destino es el registro, y envolver en un
 * enlace una tarjeta que además contiene otro enlace (`/franquicias`)
 * daría enlaces anidados —HTML inválido, y un clic cuyo resultado depende
 * de dónde caiga el ratón—. La acción es explícita y está abajo, después
 * del precio.
 *
 * ── Qué se ve, y en qué orden ────────────────────────────────────────
 * Nombre → qué incluye → precio y ahorro → volume points → acción. El
 * precio va DESPUÉS del contenido a propósito: preguntado antes de saber
 * qué trae el paquete, cualquier cifra parece cara.
 *
 * ── El texto viene traducido del servidor ────────────────────────────
 * `contentFor(pkg.translations, locale)`. El nombre de un paquete lo
 * escribe quien administra el catálogo, así que no vive en los archivos
 * de traducción de la interfaz. Cuando falta el inglés, `contentFor` cae
 * al español solo: información incompleta, nunca un hueco en blanco.
 *
 * ── A dónde lleva elegir ─────────────────────────────────────────────
 * A `/register` con el paquete en la cadena de consulta. Ver
 * `registerHrefFor`: hoy el registro ignora el parámetro, y aun así el
 * enlace lo lleva desde ya.
 */
export function PackageCard({
  pkg,
  currency,
  locale,
  numberLocale,
  productsById,
}: PackageCardProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  /*
   * ── A REGISTRO solo si NO hay sesión (Anahí, 27-ago — bug real) ────
   * "Elegir este paquete" mandaba SIEMPRE a `/register`, así que a una
   * socia ya dentro la sacaba a crear una cuenta que ya tiene. Con
   * sesión, el paquete lleva al flujo de compra real —adquirir la
   * franquicia con su primer stock— que es donde se genera el pago.
   */
  /*
   * ── El paquete elegido VIAJA (Anahí, 29-ago) ───────────────────────
   * Antes se mandaba a `/franquicias/adquirir` a secas y la pantalla no
   * tenía forma de saber qué se había elegido: pedía volver a escogerlo
   * en un modal. Con el slug en la URL, la pantalla abre ya con ese
   * paquete a la derecha —foto, nombre y precio— y la franquicia a la
   * izquierda, que es la comparación que hace falta para decidir.
   */
  const elegirHref = isAuthenticated
    ? `/franquicias/adquirir?paquete=${encodeURIComponent(pkg.slug)}`
    : registerHrefFor(pkg.slug);
  const content = contentFor(pkg.translations, locale);
  const viewer = usePriceViewer();
  const price = packagePriceFor(pkg, currency, viewer);
  const items = resolveItems(pkg.items, productsById);
  const franchiseePercent = packageFranchiseeDiscountPercentOf(pkg);

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-ds-xl border border-ds-border bg-white shadow-ds-xs transition-shadow duration-300 hover:shadow-ds-card">
      {/*
        ── La imagen del paquete, arriba (Anahí, 28-ago) ────────────────
        Como en producción: el arte del paquete a todo el ancho antes del
        nombre. Sin arte propio, el mosaico de sus productos sobre el
        panel lavanda — nunca una tarjeta sin imagen.
      */}
      <PackageArtwork imageUrl={pkg.imageUrl} name={content.name} items={items} />

      <div className="flex flex-1 flex-col p-6 sm:p-7">
      {/*
        ── El título entero, los distintivos DEBAJO (Anahí, 29-ago) ─────
        "Acomoda mejor los títulos, deben verse bien, y pon abajo los
        componentes."

        Estaban en la misma fila, y el distintivo de categoría —"NEGOCIO
        DIGITAL (MAYORISTA)", monoespaciado y con `tracking` ancho— no
        cede espacio: es `shrink-0` porque partirlo en dos renglones lo
        haría ilegible. En una tarjeta de tres columnas eso dejaba al
        nombre con cuatro caracteres de ancho y lo rompía letra por
        letra: "ZENIT · Paquet / e de Alto Impact / o".

        Apilados, el nombre dispone del ancho completo y los distintivos
        se acomodan solos debajo, envolviendo si hace falta. Se lee
        primero QUÉ es y luego sus etiquetas, que es el orden natural.
      */}
      <header>
        {content.tagline && (
          <p className="mb-1 text-[13px] leading-snug text-ds-ink-3">{content.tagline}</p>
        )}
        <h3 className="text-balance break-words text-[20px] font-semibold leading-tight tracking-tight text-ds-ink">
          {content.name}
        </h3>

        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <PackageDiscountBadge percent={effectiveDiscountPercent(pkg, currency)} />
          <span
            className={
              pkg.category === "franchisee"
                ? "rounded-full bg-ds-ink px-2.5 py-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-white"
                : "rounded-full bg-ds-lavender-bg px-2.5 py-1 font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-ds-violet-pop"
            }
          >
            {t(PACKAGE_CATEGORY_LABELS[pkg.category])}
          </span>
        </div>
      </header>

      <div className="mt-5">
        <PackageContents items={items} locale={locale} />
      </div>

      {content.benefits && content.benefits.length > 0 && (
        <div className="mt-5">
          <p className="text-[13px] font-semibold text-ds-ink">{t("Beneficios")}</p>
          <ul className="mt-2 space-y-1.5">
            {content.benefits.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2 text-[13.5px] leading-snug text-ds-ink-3">
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-ds-violet-pop" />
                <span className="min-w-0 break-words">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Divider className="my-6" />

      <PackagePrice
        price={price}
        numberLocale={numberLocale}
        requestedCurrency={currency}
      />

      <div className="mt-5">
        <PackageVolumePoints points={pkg.volumePoints} units={totalUnits(pkg.items)} />
      </div>

      {/*
        ── El 10 % de franquiciatario (Anahí, 14-sep) ─────────────────────
        Todo paquete lo tiene (el general, o el suyo). A quien no tiene
        franquicia se le dice cuánto se ahorraría; a quien la tiene, el
        precio de arriba YA lo lleva y se le dice.
      */}
      {franchiseePercent > 0 && (
        <div className="mt-4">
          {viewer.isFranchisee ? (
            <p className="inline-flex rounded-full bg-ds-lavender-bg px-3 py-1.5 text-[12.5px] font-semibold text-ds-violet-pop">
              {t("Tu precio de franquiciatario · {{n}}% menos", { n: franchiseePercent })}
            </p>
          ) : (
            <PackageFranchiseeNote percent={franchiseePercent} />
          )}
        </div>
      )}

      {/* `mt-auto` pega la acción al fondo: en una fila de tres tarjetas
          de alto distinto, los botones quedan alineados entre sí en vez
          de flotar cada uno donde acabe su texto. */}
      <div className="mt-auto pt-6">
        {/*
          El texto visible es corto y el nombre accesible completo: tres
          botones que se anuncian "Elegir paquete, Elegir paquete, Elegir
          paquete" no le sirven a quien navega saltando entre enlaces. Y
          el nombre dentro del botón tampoco vale: `Button` no parte
          líneas (`whitespace-nowrap`), así que un paquete de nombre largo
          se saldría de la tarjeta.
        */}
        <Button
          as={Link}
          to={elegirHref}
          size="lg"
          fullWidth
          aria-label={t("Elegir {{paquete}}", { paquete: content.name })}
          rightIcon={<ArrowRight aria-hidden="true" />}
        >
          {t("Elegir este paquete")}
        </Button>
      </div>
      </div>
    </article>
  );
}
